import React, {
  Fragment,
  memo,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { Menu, Transition } from '@headlessui/react';
import { SVGIcon } from '~/components/SVGIcons';
import { useDispatch, useSelector } from 'react-redux';

export type Options = { id: string; name: string; target: string };

interface IDropdown {
  label: string;
  title?: string;
  isRounded?: boolean;
  warning?: boolean;
  disabled?: boolean;
  options?: Options[];
  currentOption?: string;
  currentSelectedOption?: Options | null;
  className?: string;
  handleSelect?: (param: string) => void;
  getData?: () => void;
  onChange?: (option: Options | null) => void;
}

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ');
}

const Dropdown: React.FC<IDropdown> = ({
  label,
  title,
  options,
  disabled,
  isRounded,
  currentOption,
  currentSelectedOption = null,
  getData,
  onChange,
  handleSelect,
  className,
  warning,
}) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState<Options | null>(null);
  const [focused, setFocused] = useState<boolean>(false);

  useLayoutEffect(() => {
    setSelectedOption(null);
  }, []);

  const handleOptionSelect = (option: Options) => {
    if (!options?.length && getData) getData();
    if (handleSelect) handleSelect(option.id);
    setSelectedOption(option);
    if (onChange) {
      onChange(option);
    }
  };

  useEffect(() => {
    if (currentSelectedOption !== null) {
      handleOptionSelect(currentSelectedOption);
    } else {
      setSelectedOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedOption]);

  return (
    <div
      className={`${isRounded ? 'flex pr-4' : 'w-full flex-col'} ${className}`}
    >
      <h1 className="pb-2 text-sm text-black">{title}</h1>
      <Menu as="div" className="relative inline-block w-full text-left">
        <div className="flex w-full">
          <Menu.Button
            disabled={disabled}
            className={`inline-flex items-center justify-between border-2 bg-transparent bg-white pl-2 text-left text-sm font-medium
            ${
              warning
                ? 'border-erro-bordocaulequente50'
                : selectedOption
                  ? 'border-neutra3-cinzaquente50'
                  : 'border-neutra3-cinzaquente70'
            }
            ${disabled && 'opacity-30'} 
            ${
              selectedOption
                ? 'text-neutra3-cinzaquente10'
                : 'text-neutra3-cinzaquente50'
            }
              ${
                isRounded
                  ? 'h-8 w-[220px] rounded-full'
                  : 'h-12 w-full rounded-md shadow-sm'
              }
               `}
          >
            {selectedOption ? selectedOption.name : label}
            <SVGIcon
              name="expand"
              color="#45433A"
              size="small"
              className="mt-4"
            />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={`absolute z-30 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg focus:outline-none`}
          >
            <div className="py-2">
              {options?.map(option => (
                <Menu.Item key={option.id}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => handleOptionSelect(option)}
                      className={classNames(
                        active
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-neutra3-cinzaquente40',
                        `block w-full px-4 py-2 text-left text-sm focus:${setFocused(
                          true,
                        )}`,
                      )}
                    >
                      {currentOption ?? option.name}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default Dropdown;
